import { InnerBox } from "@/components/elements/InnerBox";
import { FC, useMemo } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { black } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { useGetUserSettingInfo } from "@/api/services/main/user-setting";
import { useLearnerList } from "@/api/services/main/learner";
import { useMe } from "@/api/services/main/auth";
import { useDialog } from "@/hooks/useDialog";
import { ConfirmHideTutorialModal } from "@/features/dashboard/components/ConfirmHideTutorialModal";
import { STEPS_TUTORIAL } from "@/features/dashboard/constants/stepTutorial";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { USER_TYPE } from "@/types/enum";
import { Link } from "react-router-dom";
import { Step, StepConnector, stepConnectorClasses, StepIconProps, StepLabel, Stepper, styled, Link as MuiLink } from "@mui/material";
import { IconCheck } from "@/assets/icons";
import { background, blue, state, text as textColor } from "@/theme/colors";
import { useRSO } from "@/api/services/main/rso";

const getStepTitleColor = (stepIndex: number, activeStep: number): string => {
  if (stepIndex < activeStep) return black[90];
  if (stepIndex === activeStep) return black[30];
  return black[70];
};
const getStepContentColor = (stepIndex: number, activeStep: number): string => {
  if (stepIndex < activeStep) return black[90];
  return black[70];
};

type TDashboardInitialSetupProps = {
  onHide?: () => void;
};

export const DashboardInitialSetup: FC<TDashboardInitialSetupProps> = ({ onHide }) => {
  const { data: meResult, isLoading: isLoadingMe } = useMe();
  const isRsoUser = meResult?.data?.type === USER_TYPE.RSO_USER;
  const isCompanyUser = meResult?.data?.type === USER_TYPE.COMPANY_USER;

  const { data: rsoInfoResult, isLoading: isLoadingRsoInfo } = useRSO(!isCompanyUser);
  const { data: userSettingResult, isLoading: isLoadingUserSetting } = useGetUserSettingInfo();
  const { data: learnerListResult, isLoading: isLoadingLearnerList } = useLearnerList({ page: 1, perPage: 1 });

  const confirmModal = useDialog();

  const isLoading = isLoadingMe || isLoadingRsoInfo || isLoadingUserSetting || isLoadingLearnerList;

  const visibleSteps = useMemo(() => {
    if (isRsoUser) return STEPS_TUTORIAL;
    if (isCompanyUser) return STEPS_TUTORIAL.slice(1);
    return STEPS_TUTORIAL;
  }, [isRsoUser, isCompanyUser]);

  const isStep1Complete = useMemo(() => {
    if (!rsoInfoResult?.data) return false;
    const rso = rsoInfoResult.data;
    const hasRsoName = !!rso.name?.trim();
    const hasOfficeName = !!rso.matchOffices?.[0]?.officeName?.trim();
    return hasRsoName && hasOfficeName;
  }, [rsoInfoResult]);

  const isStep2Complete = useMemo(() => {
    const hasLearner = (learnerListResult?.pagination?.total ?? 0) > 0;
    return hasLearner;
  }, [learnerListResult]);

  const isStep3Complete = useMemo(() => {
    if (!userSettingResult?.data) return false;
    return !!userSettingResult.data.userFullName?.trim();
  }, [userSettingResult]);

  const activeStep = useMemo(() => {
    if (isRsoUser) {
      if (!isStep1Complete) return 0;
      if (!isStep2Complete) return 1;
      if (!isStep3Complete) return 2;
      return visibleSteps.length;
    }
    // Company user: steps start from index 1 (learner, user setting, complete)
    if (!isStep2Complete) return 0;
    if (!isStep3Complete) return 1;
    return visibleSteps.length;
  }, [isRsoUser, isStep1Complete, isStep2Complete, isStep3Complete, visibleSteps]);
  const userSettingStepIndex = isRsoUser ? 1 : 0;

  const handleCloseTutorial = async () => {
    const ok = await confirmModal.show();
    if (ok) {
      onHide?.();
    }
  };


  if (isLoading) {
    return (
      <InnerBox title="irohana studyをご利用いただくための初期設定手順" variantTitle="head20Bold" bordered={true} showDivider={false} gap={2}>
        <LoadingArea height={200}/>
      </InnerBox>
    );
  }

  return (
    <InnerBox title="irohana studyをご利用いただくための初期設定手順" variantTitle="head20Bold" bordered={true} showDivider={false} gap={2}>
      <Stack width={"100%"}>
        <StyledStepper
          activeStep={activeStep}
          orientation="vertical"
        >
          {visibleSteps.map((step, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            const isLastStep = index === visibleSteps.length - 1;
            const isSecondStep = index === userSettingStepIndex;

            return (
              <CustomStep
                key={step.title}
                completed={isCompleted}
                active={isActive}
                className={`${isLastStep ? "last-step" : ""} ${isSecondStep ? "second-step" : ""}`}
              >
                <StyledStepLabel StepIconComponent={ColorLibStepIcon} isLastStep={isLastStep}>
                  <Stack mt={-0.5} ml={2} gap={1}>
                    <Typography variant="head20Semi" color={getStepTitleColor(index, activeStep)}>{step.title}</Typography>
                    <Stack>
                      {step.content.map((line, lineIndex) => (
                        <Typography
                          key={lineIndex}
                          variant="body14"
                          color={getStepContentColor(index, activeStep)}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Stack>
                    {step.links.length > 0 && (
                      <Stack direction="row" spacing={3} flexWrap="wrap">
                        {step.links.map((link, linkIndex) => (
                          <StyledStepLink
                            component={Link}
                            key={linkIndex}
                            to={link.url}
                            variant="body14"
                          >
                            {link.label}
                          </StyledStepLink>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </StyledStepLabel>
                {!isLastStep && <ColorLibConnector isSecondStep={isSecondStep} isCompleted={isCompleted} />}
              </CustomStep>
            );
          })}
        </StyledStepper>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button variant="outline" size="sm" onClick={handleCloseTutorial}>
            このチュートリアルを閉じる
          </Button>
        </Box>
      </Stack>

      <ConfirmHideTutorialModal open={confirmModal.open} onClose={confirmModal.cancel} onConfirm={confirmModal.confirm} />
    </InnerBox>
  );
};

// Styled components

const CustomStep = styled(Step)(() => ({
  padding: 0,
  marginBottom: 0,
  position: "relative",
  "& .MuiStepContent-root": {
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: 24,
    borderLeft: "none",
  },
  "& .MuiStepLabel-root": {
    padding: 0,
    paddingBottom: 0,
  },
  "& .MuiStepLabel-iconContainer": {
    paddingRight: 0,
  },
}));

const ColorLibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ ownerState }) => ({
  backgroundColor: background.disable,
  zIndex: 1,
  color: textColor.tertiary,
  width: 40,
  height: 40,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  ...(ownerState.active && {
    backgroundColor: background.white,
    border: `4px solid ${blue[80]}`,
    color: blue[80],
  }),
  ...(ownerState.completed && {
    backgroundColor: blue[80],
    color: textColor.white,
  }),
}));

const ColorLibConnector = styled(StepConnector)<{ isSecondStep?: boolean; isCompleted?: boolean }>(({ isSecondStep, isCompleted }) => ({
  marginLeft: 18,
  marginTop: isSecondStep ? -75 : -60,
  marginBottom: isSecondStep ? -18 : -12,
  [`& .${stepConnectorClasses.line}`]: {
    minHeight: 140,
    borderLeft: `4px solid ${isCompleted ? blue[80] : background.disable}`,
  },
}));

const StyledStepLabel = styled(StepLabel)<{ isLastStep?: boolean }>(({ isLastStep }) => ({
  alignItems: "flex-start",
  padding: 0,
  paddingBottom: isLastStep ? 0 : 3,
}));

const StyledStepper = styled(Stepper)(() => ({
  "& > .MuiStepConnector-root": {
    display: "none",
  },
  marginTop: 1,
}));

const StyledStepLink = styled(MuiLink)(() => ({
  color: state.link_1,
  cursor: "pointer",
  textDecoration: "underline",
  "&:hover": {
    color: state.link_1,
  },
})) as typeof MuiLink;

const ColorLibStepIcon = (props: StepIconProps) => {
  const { active, completed, className, icon } = props;

  return (
    <ColorLibStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <IconCheck width={20} height={20} /> : <Typography variant="sub16Semi">{icon}</Typography>}
    </ColorLibStepIconRoot>
  );
};
